/**
 * User-defined command aliases. Stored in localStorage so each browser
 * has its own set, and they survive reloads.
 *
 * Usage from the terminal:
 *   /alias                    — list all aliases
 *   /alias NAME = /cmd ...    — define (or overwrite) NAME → command
 *   /alias rm NAME            — delete
 *   /alias clear              — wipe all
 *
 * Then just type `/NAME` to execute the stored command. Works with
 * pipes, so `/alias morning = /indices | /sectors | /vix` chains
 * three commands into one shortcut.
 */

const STORAGE_KEY = 'terminal_aliases_v1'

export function loadAliases(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    // non-fatal
  }
  return {}
}

export function saveAliases(map: Record<string, string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // non-fatal
  }
}

/** Normalise `/alias morning=/indices | /sectors` → ['morning', '/indices | /sectors'] */
export function parseAliasDefinition(args: string): [string, string] | null {
  const eq = args.indexOf('=')
  if (eq < 0) return null
  const name = args.slice(0, eq).trim().replace(/^\//, '')
  const cmd = args.slice(eq + 1).trim()
  if (!name || !cmd) return null
  if (!/^[a-z0-9_-]+$/i.test(name)) return null
  return [name, cmd]
}

export interface AliasResult {
  reply: string
  refresh?: boolean
}

export function handleAliasCommand(args: string): AliasResult {
  const trimmed = args.trim()
  const current = loadAliases()

  if (!trimmed) {
    // List aliases
    const names = Object.keys(current).sort()
    if (names.length === 0) {
      return {
        reply:
          '  No aliases defined.\n  Define one with:  /alias NAME = /cmd [| /cmd ...]\n  Example:  /alias morning = /indices | /sectors | /vix',
      }
    }
    const longest = Math.max(...names.map(n => n.length))
    const lines = ['  User-defined aliases:', '']
    for (const n of names) {
      lines.push(`  /${n.padEnd(longest)}  →  ${current[n]}`)
    }
    lines.push('')
    lines.push(`  ${names.length} alias${names.length === 1 ? '' : 'es'}. Use /alias rm NAME to remove.`)
    return { reply: lines.join('\n') }
  }

  if (trimmed === 'clear') {
    saveAliases({})
    return { reply: '  All aliases cleared.', refresh: true }
  }

  const rmMatch = trimmed.match(/^(?:rm|del|remove)\s+\/?([a-z0-9_-]+)$/i)
  if (rmMatch) {
    const name = rmMatch[1]
    if (!(name in current)) {
      return { reply: `  /${name} is not an alias.` }
    }
    const next = { ...current }
    delete next[name]
    saveAliases(next)
    return { reply: `  Removed /${name}.`, refresh: true }
  }

  // Definition form: NAME = CMD
  const parsed = parseAliasDefinition(trimmed)
  if (!parsed) {
    return {
      reply:
        '  Usage:\n' +
        '    /alias                       list aliases\n' +
        '    /alias NAME = /cmd [| ...]   define alias\n' +
        '    /alias rm NAME               remove alias\n' +
        '    /alias clear                 wipe all',
    }
  }
  const [name, cmd] = parsed
  const next = { ...current, [name]: cmd }
  saveAliases(next)
  return { reply: `  Alias /${name} → ${cmd}`, refresh: true }
}

/**
 * If `cmd` is an alias (e.g. `/morning` or `/morning AAPL`), returns the
 * expanded string with any trailing args appended. Otherwise returns null.
 */
export function expandAlias(cmd: string): string | null {
  const m = cmd.match(/^\/([a-z0-9_-]+)(\s+(.*))?$/i)
  if (!m) return null
  const name = m[1]
  const rest = m[3]?.trim() ?? ''
  const aliases = loadAliases()
  if (!(name in aliases)) return null
  const target = aliases[name]
  return rest ? `${target} ${rest}` : target
}
